import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, Row, Col } from 'antd';

import Permissions from '../auth/Permissions';
import BreadCrumbContainer from '../common/breadcrumbContainer/breadcrumbContainer';

import ProjectList from './projectList';
import {
  getProjects,
  getProjectEquipment,
  getProjectFileTemplates,
  getProjectTypes,
  getProjectLabels,
  getUserProjectFilterViews,
  updateProjectFilters,
  favouriteUserProjectFilterView,
  getProjectCustomFieldTemplate,
  fetchFilteredProjectIds,
} from './state/projects.actions';
import {
  getCategories,
  getTemplates as getCostcodeTemplates,
} from '../costcodes/state/costcodes.actions';
import { getProgress } from '../reports/state/reports.actions';
import { getEquipment, getStatuses as getEquipmentStatuses, getEquipmentTypes } from '../equipment/state/equipment.actions';
import { getCustomerLabels, getCustomers } from '../contacts/customers/state/customers.actions';
import { getLabels } from '../labels/state/labels.actions';
import { getBillingRates } from '../billingRates/state/billingRates.actions';
import {
  getTemplates,
  getStatuses,
  getCustomTables,
} from '../forms/state/forms.actions';
import {
  getEmails,
} from '../emails/state/email.actions';
import { getUserLabels } from '../users/state/users.actions';
import { createNuxEntry } from '../nux/state/nux.actions';
import { getCardLinks } from '../boards/state/boards.actions';
import {
  getInvoices,
  getInvoiceStatusLabels,
} from '../payables/invoices/state/invoices.actions';
import { getBuckets, getBucketTemplates } from '../buckets/state/buckets.actions';

import { PROJECT_GENERAL_TYPE } from '../nux/nux.constants';
import { getIdMap } from '../helpers/helpers';
import { getSavedAnalyticsReports } from '../analytics/state/analytics.actions';
import { getSavedDashboards } from '../dashboard/state/dashboard.actions';

import { PROJECT_ANALYTICS_TYPE } from '../analytics/analytics.constants';
import { filterProjects } from './projectHelpers';
import { defaultFilters, isFilterActive } from './projectFilters';

export default function Projects(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const nux = useSelector((state) => state.nux.nux);
  const projectTypes = useSelector((state) => state.projects.projectTypes);
  const projects = useSelector((state) => state.projects.projects);
  const users = useSelector((state) => state.users.users);
  const projectFilterViews = useSelector((state) => state.projects.projectFilterViews);
  const projectFilters = useSelector((state) => state.projects.projectFilters);
  const projectToLabel = useSelector((state) => state.projects.projectToLabel);
  const labels = useSelector((state) => state.labels);
  const filteredProjectIds = useSelector((state) => state.projects.filteredProjectIds);
  const buckets = useSelector((state) => state.buckets.buckets);
  const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);

  const [crumbs, setCrumbs] = useState([]);
  const [filterViewsLoaded, setFilterViewsLoaded] = useState(false);

  const { typeId } = useParams();
  const appliedFilterForTypeRef = useRef(null);
  const projectFilterViewsRef = useRef(projectFilterViews);

  const projectTypeMap = useMemo(() => getIdMap(projectTypes), [projectTypes]);
  const myUser = useMemo(() => users?.find((user) => user.id === Permissions.id), [users]);

  // Get projects the user has permission to view
  const permittedProjects = useMemo(() => {
    const { projects: userProjects = [] } = myUser ?? {};
    const userProjectSet = new Set(userProjects.map(({ id }) => id));
    let fp = projects;
    if (Permissions.has('PROJECTS_VIEW_ASSIGNED') && !Permissions.has('PROJECTS_VIEW_ALL') && projects?.length) {
      fp = projects.filter((project) => userProjectSet.has(project.id));
    }
    return fp;
  }, [projects, myUser]);

  // Get filter views for the current type
  const relevantFilterViews = useMemo(() => (
    projectFilterViews?.filter(
      (view) => (!typeId && !view.typeId) || (typeId && view.typeId === typeId),
    ) ?? []
  ), [projectFilterViews, typeId]);

  // Apply filters to projects
  const filteredProjects = useMemo(() => filterProjects({
    projects: permittedProjects,
    filters: projectFilters,
    projectToLabel,
    typeId,
    filteredProjectIds,
    buckets,
    bucketTemplates,
  }), [permittedProjects, projectFilters, projectToLabel, typeId, filteredProjectIds, buckets, bucketTemplates]);

  // Check if any filter differs from defaults
  const filterActive = useMemo(() => isFilterActive(projectFilters), [projectFilters]);

  // Apply favourite filter when filter views are loaded or type changes
  const applyFavouriteFilter = useCallback(async (views, type) => {
    const favouriteView = views?.find(
      (view) => view.isFavourite && (view.typeId === type || (!view.typeId && !type)),
    );
    if (favouriteView) {
      const { id: filterViewId, ...restFilters } = favouriteView;

      // Build custom field filters for backend
      const customFilters = {};
      const standardKeys = new Set([
        'filterViewName', 'isFavourite', 'typeId', 'labels', 'contractExpiration', 'active', 'projectTypeId',
      ]);
      Object.entries(restFilters).forEach(([key, value]) => {
        if (!standardKeys.has(key) && Array.isArray(value) && value.length > 0) {
          customFilters[key] = value;
        }
      });

      if (Object.keys(customFilters).length > 0) {
        await dispatch(fetchFilteredProjectIds(customFilters));
      } else {
        await dispatch(fetchFilteredProjectIds(null));
      }
      dispatch(updateProjectFilters({
        ...restFilters,
        filterView: filterViewId,
      }));
    } else {
      await dispatch(fetchFilteredProjectIds(null));
      // Reset to default filters when no favourite exists for this type
      dispatch(updateProjectFilters({
        ...defaultFilters,
        labels: [],
        filterView: null,
        filterViewName: null,
        isFavourite: false,
      }));
    }
  }, [dispatch]);

  // Handle favourite toggle
  const onFavourite = useCallback((filterViewId, isFavourite) => {
    dispatch(favouriteUserProjectFilterView(filterViewId, isFavourite));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getProjects());
    dispatch(getProgress());
    dispatch(getCategories());
    dispatch(getEquipment());
    dispatch(getProjectEquipment());
    dispatch(getLabels());
    dispatch(getCustomers());
    dispatch(getCustomerLabels());
    dispatch(getBillingRates());
    dispatch(getTemplates());
    dispatch(getEmails());
    dispatch(getStatuses());
    dispatch(getCustomTables());
    dispatch(getUserLabels());
    dispatch(getCardLinks());
    dispatch(getCostcodeTemplates());
    dispatch(getInvoices());
    dispatch(getInvoiceStatusLabels());
    dispatch(getProjectFileTemplates());
    dispatch(getEquipmentStatuses());
    dispatch(getProjectTypes());
    dispatch(getProjectLabels());
    if (Permissions.has('MANAGE_ANALYTICS')) {
      dispatch(getSavedAnalyticsReports(PROJECT_ANALYTICS_TYPE));
    }
    dispatch(getSavedDashboards(PROJECT_ANALYTICS_TYPE));
    dispatch(getEquipmentTypes());
    dispatch(getProjectCustomFieldTemplate());
    dispatch(getBucketTemplates());
    dispatch(getBuckets());
    dispatch(getUserProjectFilterViews()).then(() => {
      setFilterViewsLoaded(true);
    });
  }, []);

  // Keep ref in sync with latest value (synchronous update ensures it's current before effects run)
  projectFilterViewsRef.current = projectFilterViews;

  useEffect(() => {
    if (filterViewsLoaded
        && projectFilterViewsRef.current
        && appliedFilterForTypeRef.current !== typeId) {
      appliedFilterForTypeRef.current = typeId;
      applyFavouriteFilter(projectFilterViewsRef.current, typeId);
    }
  }, [filterViewsLoaded, typeId]);

  useEffect(() => {
    const relevantProjectType = projectTypeMap[typeId];
    setCrumbs([{
      text: relevantProjectType?.name ? `${t('Project', { count: 2 })} - ${relevantProjectType?.name}` : t('Project', { count: 2 }),
      icon: relevantProjectType?.icon ?? 'project',
    }]);
  }, [typeId, projectTypeMap]);

  useEffect(() => {
    if (!nux?.has(PROJECT_GENERAL_TYPE)) {
      dispatch(createNuxEntry(PROJECT_GENERAL_TYPE));
    }
  }, [nux]);

  const hasProjectPermissions = Permissions.has('PROJECTS_WRITE')
    || Permissions.has('PROJECTS_VIEW_ALL')
    || Permissions.has('PROJECTS_VIEW_ASSIGNED');

  if (!hasProjectPermissions) return <Redirect to="/dashboard" />;
  return (
    <BreadCrumbContainer crumbs={crumbs}>
      {filterViewsLoaded ? (
        <ProjectList
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          type={typeId}
          projects={filteredProjects}
          permittedProjects={permittedProjects}
          projectFilterViews={relevantFilterViews}
          projectFilters={projectFilters}
          onFavourite={onFavourite}
          labels={labels}
          filterActive={filterActive}
        />
      ) : (
        <Row justify="center" align="middle" style={{ height: '100%', paddingBottom: 125 }}>
          <Col>
            <Spin />
          </Col>
        </Row>
      )}
    </BreadCrumbContainer>
  );
}
